// Everything derived from the data and never stored: show order, cue lists with flags, standby
// coverage and text (docs/format/README.md §6–9).

import { referencePoint } from "./geometry.ts";
import { compareCueNumbers } from "./numbers.ts";
import type { Anchor, Cue, Department, Doc, Moment, Note, NoteLayer, Page, Point, Standby } from "./types.ts";

const FALLBACK_PAGE: Page = { page: 0, rotate: 0, view: [0, 0, 612, 792], label: "" };
const cmpStr = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

export const sortDepartments = (ds: readonly Department[]): Department[] =>
  [...ds].sort((a, b) => a.order - b.order || cmpStr(a.code, b.code) || cmpStr(a.id, b.id));

export const sortLayers = (ls: readonly NoteLayer[]): NoteLayer[] =>
  [...ls].sort((a, b) => a.order - b.order || cmpStr(a.name, b.name) || cmpStr(a.id, b.id));

/** Lookups built once per document version. */
export class DocIndex {
  readonly pages = new Map<number, Page>();
  readonly departments = new Map<string, Department>();
  readonly deptRank = new Map<string, number>();
  readonly layers = new Map<string, NoteLayer>();
  readonly moments = new Map<string, Moment>();
  readonly cuesByMoment = new Map<string, Cue[]>();
  readonly sortedDepartments: Department[];
  readonly sortedLayers: NoteLayer[];
  readonly doc: Doc;

  constructor(doc: Doc) {
    this.doc = doc;
    for (const p of doc.pages) this.pages.set(p.page, p);
    this.sortedDepartments = sortDepartments(doc.departments);
    this.sortedDepartments.forEach((d, i) => {
      this.departments.set(d.id, d);
      this.deptRank.set(d.id, i);
    });
    this.sortedLayers = sortLayers(doc.noteLayers);
    for (const l of doc.noteLayers) this.layers.set(l.id, l);
    for (const m of doc.moments) this.moments.set(m.id, m);
    for (const c of doc.cues) {
      const list = this.cuesByMoment.get(c.moment);
      if (list) list.push(c);
      else this.cuesByMoment.set(c.moment, [c]);
    }
    for (const list of this.cuesByMoment.values()) list.sort((a, b) => this.compareCuesInMoment(a, b));
  }

  page(n: number): Page {
    return this.pages.get(n) ?? { ...FALLBACK_PAGE, page: n };
  }

  /** Display label of a cue's department, e.g. `LX`; `?` when the department is missing. */
  code(deptId: string): string {
    return this.departments.get(deptId)?.code ?? "?";
  }

  compareCuesInMoment(a: Cue, b: Cue): number {
    const ra = this.deptRank.get(a.department) ?? Number.MAX_SAFE_INTEGER;
    const rb = this.deptRank.get(b.department) ?? Number.MAX_SAFE_INTEGER;
    return ra - rb || compareCueNumbers(a.number, b.number) || cmpStr(a.id, b.id);
  }

  /** A moment's cues in department order, then number. */
  cuesOf(momentId: string): Cue[] {
    return this.cuesByMoment.get(momentId) ?? [];
  }

  refPoint(anchor: Anchor): Point {
    return referencePoint(this.page(anchor.page), anchor);
  }

  /** Show order comparator for anything anchored (moments, standbys, notes). */
  compareAnchored = (a: { id: string; anchor: Anchor }, b: { id: string; anchor: Anchor }): number => {
    if (a.anchor.page !== b.anchor.page) return a.anchor.page - b.anchor.page;
    const pa = this.refPoint(a.anchor);
    const pb = this.refPoint(b.anchor);
    return pa[1] - pb[1] || pa[0] - pb[0] || cmpStr(a.id, b.id);
  };

  /** Moments in show order. */
  orderedMoments(): Moment[] {
    return [...this.doc.moments].sort(this.compareAnchored);
  }

  /** Every cue in show order: moments in show order, each moment's cues in department order. */
  showCues(): Cue[] {
    return this.orderedMoments().flatMap((m) => this.cuesOf(m.id));
  }

  cueRef(c: Cue): string {
    return `${this.code(c.department)} ${c.number}`;
  }

  /** The printed label of a page, or its PDF page number when it has none. */
  pageLabel(n: number): string {
    return this.page(n).label || String(n);
  }

  hasPageLabels(): boolean {
    return this.doc.pages.some((p) => p.label !== "");
  }
}

export interface CueRow {
  cue: Cue;
  moment: Moment;
  department: Department | undefined;
  code: string;
  page: number;
  pageLabel: string;
  /** The other cues on the same moment, e.g. `SFX 7, FLY 3`. */
  with: string;
  duplicate: boolean;
  /** The previous cue in this department's numeric or letter-only sequence when this one is lower. */
  outOfSequenceAfter: string | null;
  /** Human-readable flags: `duplicate`, `out of sequence (after LX 15)`. */
  flags: string[];
}

/** The whole show's cues in show order with their flags. Filter by department afterwards. */
export function cueRows(ix: DocIndex): CueRow[] {
  const cues = ix.showCues();
  // Equal numbers (5.1 vs 5.10) count as duplicates: group by department, compare with the comparator.
  const byDept = new Map<string, Cue[]>();
  for (const c of cues) {
    const list = byDept.get(c.department);
    if (list) list.push(c);
    else byDept.set(c.department, [c]);
  }
  const dupIds = new Set<string>();
  for (const list of byDept.values()) {
    const sorted = [...list].sort((a, b) => compareCueNumbers(a.number, b.number));
    for (let i = 1; i < sorted.length; i++) {
      const a = sorted[i - 1] as Cue;
      const b = sorted[i] as Cue;
      if (compareCueNumbers(a.number, b.number) === 0) {
        dupIds.add(a.id);
        dupIds.add(b.id);
      }
    }
  }
  const prevNumericInDept = new Map<string, Cue>();
  const prevLettersInDept = new Map<string, Cue>();
  const numericStart = /^[0-9]/;
  return cues.map((cue) => {
    const moment = ix.moments.get(cue.moment) as Moment;
    // Numeric suffixes (5A) stay in the numeric sequence; letter-only cues run independently.
    const prevInDept = numericStart.test(cue.number) ? prevNumericInDept : prevLettersInDept;
    const prev = prevInDept.get(cue.department);
    prevInDept.set(cue.department, cue);
    const outOfSequenceAfter = prev && compareCueNumbers(cue.number, prev.number) < 0 ? ix.cueRef(prev) : null;
    const duplicate = dupIds.has(cue.id);
    const flags: string[] = [];
    if (duplicate) flags.push("duplicate");
    if (outOfSequenceAfter) flags.push(`out of sequence (after ${outOfSequenceAfter})`);
    return {
      cue,
      moment,
      department: ix.departments.get(cue.department),
      code: ix.code(cue.department),
      page: moment.anchor.page,
      pageLabel: ix.pageLabel(moment.anchor.page),
      with: ix
        .cuesOf(cue.moment)
        .filter((o) => o.id !== cue.id)
        .map((o) => ix.cueRef(o))
        .join(", "),
      duplicate,
      outOfSequenceAfter,
      flags,
    };
  });
}

export interface NoteRow {
  note: Note;
  layer: NoteLayer | undefined;
  page: number;
  pageLabel: string;
}

/** Every note in show order. Filter by layer afterwards. */
export function noteRows(ix: DocIndex): NoteRow[] {
  return [...ix.doc.notes].sort(ix.compareAnchored).map((note) => ({ note, layer: ix.layers.get(note.layer), page: note.anchor.page, pageLabel: ix.pageLabel(note.anchor.page) }));
}

export interface StandbyInfo {
  standby: Standby;
  /** Covered cues in show order. */
  covered: Cue[];
}

/** The cues that come after `at` (a standby's place) in show order. */
export function cuesAfter(ix: DocIndex, at: { id: string; anchor: Anchor }): Cue[] {
  return ix.showCues().filter((c) => ix.compareAnchored(ix.moments.get(c.moment) as Moment, at) > 0);
}

/**
 * The cues after `at`, stopping before the next standby (other than `at` itself) and, with
 * `pages`, after that many pages past `at`'s page. Format 1's coverage rule, and the suggestion
 * for a new standby's last cue.
 */
export function cuesUntilNextStandby(ix: DocIndex, at: { id: string; anchor: Anchor }, pages = Number.POSITIVE_INFINITY): Cue[] {
  const next = ix.doc.standbys.filter((s) => s.id !== at.id && ix.compareAnchored(s, at) > 0).sort(ix.compareAnchored)[0];
  return cuesAfter(ix, at).filter((c) => {
    const m = ix.moments.get(c.moment) as Moment;
    return (!next || ix.compareAnchored(m, next) < 0) && m.anchor.page <= at.anchor.page + pages;
  });
}

/**
 * Each standby, in show order, with the cues it covers: every cue of every department after it in
 * show order, up to and including its `through` cue. None when `through` is empty, missing, or
 * comes before the standby.
 */
export function standbyCoverage(ix: DocIndex): StandbyInfo[] {
  const cues = ix.showCues();
  const position = new Map(cues.map((c, i) => [c.id, i]));
  return [...ix.doc.standbys].sort(ix.compareAnchored).map((standby) => {
    const end = position.get(standby.through);
    if (end === undefined) return { standby, covered: [] };
    const start = cues.findIndex((c) => ix.compareAnchored(ix.moments.get(c.moment) as Moment, standby) > 0);
    return { standby, covered: start < 0 || start > end ? [] : cues.slice(start, end + 1) };
  });
}

/** `5`, `5, 6`, `5–8`, or every number when they don't strictly increase. */
function numbersText(numbers: string[]): string {
  if (numbers.length >= 3) {
    let increasing = true;
    for (let i = 1; i < numbers.length; i++) {
      if (compareCueNumbers(numbers[i - 1] as string, numbers[i] as string) >= 0) increasing = false;
    }
    if (increasing) return `${numbers[0]}–${numbers[numbers.length - 1]}`;
  }
  return numbers.join(", ");
}

/**
 * The standby's text, e.g. `SB LX 14–16 · SFX 7`. With a department filter, only those departments'
 * parts are kept and null means "don't draw it". Without a filter an empty standby reads `SB —`.
 */
export function standbyText(ix: DocIndex, covered: Cue[], departments?: ReadonlySet<string>): string | null {
  const parts: string[] = [];
  for (const d of ix.sortedDepartments) {
    if (departments && !departments.has(d.id)) continue;
    const numbers = covered.filter((c) => c.department === d.id).map((c) => c.number);
    if (numbers.length) parts.push(`${d.code} ${numbersText(numbers)}`);
  }
  if (parts.length) return `SB ${parts.join(" · ")}`;
  return departments ? null : "SB —";
}
